import { useMemo, useState } from 'react';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { AsyncButton } from '@/components/common/AsyncButton';
import type {
  EodNetInstrumentDto,
  EodPositionBreakdownDto,
  ExpectedEodSwapSnapshotDto,
  InstrumentSwapBreakdownDto,
} from '@/api/types/riskDashboard';

type ViewMode = 'clients' | 'lps' | 'net';

// renderEodSwapSigned: NA if null; 0.00 near-zero; else +/- abs, green/red.
function signedSwap(v: number | null | undefined) {
  if (v == null || Number.isNaN(v)) return <span className="text-muted">NA</span>;
  if (Math.abs(v) < 0.00005) return <span>0.00</span>;
  const sign = v > 0 ? '+' : '-';
  const cls = v > 0 ? 'text-success' : 'text-danger';
  return <span className={cls}>{sign}{Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
}
function pct(v: number | null | undefined) {
  if (v == null || Number.isNaN(v)) return <span className="text-muted">NA</span>;
  return <span>{(v * 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</span>;
}
function vol(v: number | null | undefined) {
  return v == null || Number.isNaN(v) ? '0.00' : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function cardTotal(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '--';
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  return `${sign}${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface RowVM {
  instrument: string;
  longVolume: number;
  shortVolume: number;
  totalSwap?: number | null;
  concentration?: number | null;
  entities: (EodPositionBreakdownDto & { source?: string })[];
}

// Presentational Expected-EOD-Swap view — shared by the live (Consolidated Exposure) and
// backdated (Overnights) tabs; the caller supplies the snapshot + recalc/export handlers.
export function EodSwapView({
  snapshot,
  loading,
  isError,
  onRecalculate,
  onExport,
  card = true,
}: {
  snapshot?: ExpectedEodSwapSnapshotDto;
  loading?: boolean;
  isError?: boolean;
  onRecalculate: () => void;
  onExport: () => void | Promise<unknown>;
  card?: boolean;
}) {
  const [viewMode, setViewMode] = useState<ViewMode>('net');
  const snap = snapshot;

  const rows = useMemo<RowVM[]>(() => {
    if (!snap) return [];
    const list: RowVM[] =
      viewMode === 'net'
        ? (snap.netRows ?? []).map((r: EodNetInstrumentDto) => ({
            instrument: r.instrument,
            longVolume: r.longVolume,
            shortVolume: r.shortVolume,
            totalSwap: r.totalSwap,
            concentration: r.concentration,
            entities: [
              ...(r.clientEntities ?? []).map((e) => ({ ...e, source: 'Client' })),
              ...(r.lpEntities ?? []).map((e) => ({ ...e, source: 'LP' })),
            ],
          }))
        : ((viewMode === 'clients' ? snap.clientRows : snap.lpRows) ?? []).map((r: InstrumentSwapBreakdownDto) => ({
            instrument: r.instrument,
            longVolume: r.longVolume,
            shortVolume: r.shortVolume,
            totalSwap: r.totalSwap,
            concentration: r.concentration,
            entities: r.entities ?? [],
          }));
    return [...list].sort((a, b) => (b.totalSwap ?? 0) - (a.totalSwap ?? 0));
  }, [snap, viewMode]);

  const columns: DataGridColumn<RowVM>[] = [
    { key: 'instrument', title: 'Instrument', className: 'text-start', value: (r) => r.instrument },
    { key: 'longVolume', title: 'Long Vol', className: 'text-end', value: (r) => r.longVolume, cell: (r) => vol(r.longVolume) },
    { key: 'shortVolume', title: 'Short Vol', className: 'text-end', value: (r) => r.shortVolume, cell: (r) => vol(r.shortVolume) },
    { key: 'totalSwap', title: 'Expected EOD Swaps', className: 'text-end', value: (r) => r.totalSwap ?? null, cell: (r) => signedSwap(r.totalSwap) },
    { key: 'concentration', title: 'Concentration', className: 'text-end', value: (r) => r.concentration ?? null, cell: (r) => pct(r.concentration) },
  ];

  const cards: { mode: ViewMode; id: string; label: string; icon: string; bg: string; total?: number | null }[] = [
    { mode: 'clients', id: 'eodSwapClientsCard', label: 'Clients Swaps', icon: 'ri-user-fill', bg: 'bg-warning', total: snap?.clientTotal },
    { mode: 'lps', id: 'eodSwapLpsCard', label: 'LP Swaps', icon: 'ri-bank-fill', bg: 'bg-primary', total: snap?.lpTotal },
    { mode: 'net', id: 'eodSwapNetCard', label: 'Net Swaps', icon: 'ri-exchange-funds-fill', bg: 'bg-info', total: snap?.netTotal },
  ];

  return (
    <div>
      {snap?.warning && (
        <div className="alert alert-warning py-2 px-3 mb-2 fs-12">
          <i className="fe fe-alert-triangle me-2" />
          {snap.warning}
        </div>
      )}

      <div className="row g-1 px-2 mb-1 mt-1">
        {cards.map((c) => (
          <div className="col-12 col-sm-6 col-md-4 px-1" key={c.id}>
            <div
              className={`card overflow-hidden cursor-pointer hover-clickable-animation${viewMode === c.mode ? ' border border-2 border-primary' : ''}`}
              role="button"
              onClick={() => setViewMode(c.mode)}
            >
              <div className="card-body">
                <div className="row">
                  <div className="col">
                    <h4 className="mb-2 fw-semibold">{cardTotal(c.total)}</h4>
                    <p className="text-muted fs-13 mb-0">{c.label}</p>
                  </div>
                  <div className="col col-auto d-flex align-items-center">
                    <span className={`ms-auto ${c.bg} rounded-circle d-inline-flex align-items-center justify-content-center`} style={{ width: 48, height: 48 }}>
                      <i className={`text-white ${c.icon} fs-5`} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <DataGrid
        columns={columns}
        data={rows}
        rowKey={(r) => r.instrument}
        card={card}
        emptyMessage={loading ? 'Loading…' : isError ? <span className="text-danger">Failed to load Expected EOD Swap.</span> : 'No data for the current filters.'}
        toolbarStart={
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={onRecalculate}>
              <i className="fa fa-sync me-1" />
              Recalculate
            </button>
            <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-download" onClick={() => onExport()}>
              Export
            </AsyncButton>
          </div>
        }
        expandable={(r) => <EodChild entities={r.entities} />}
      />
    </div>
  );
}

// Per-login drill with a Long/Short pill toggle (rebuilds the legacy childHtml).
function EodChild({ entities }: { entities: (EodPositionBreakdownDto & { source?: string })[] }) {
  const [side, setSide] = useState<'long' | 'short'>('long');
  const hasSource = entities.some((e) => e.source);
  return (
    <div className="rd-child-panel">
      <div className="eod-pill-group btn-group btn-group-sm mb-2" role="group">
        <button type="button" className={`btn eod-child-filter ${side === 'long' ? 'btn-primary active' : 'btn-outline-primary'}`} onClick={() => setSide('long')}>
          Long
        </button>
        <button type="button" className={`btn eod-child-filter ${side === 'short' ? 'btn-primary active' : 'btn-outline-primary'}`} onClick={() => setSide('short')}>
          Short
        </button>
      </div>
      <div className="rd-contributors-scroll">
        <table className="nop-inner-table table table-sm table-hover text-center my-1 mx-0">
          <thead>
            <tr>
              {hasSource && <th>Source</th>}
              <th>Login</th>
              <th className="eod-vol-header">{side === 'long' ? 'Long Volume' : 'Short Volume'}</th>
              <th>Swap</th>
            </tr>
          </thead>
          <tbody>
            {entities.length === 0 ? (
              <tr>
                <td colSpan={hasSource ? 4 : 3} className="text-center text-muted py-2">
                  No positions
                </td>
              </tr>
            ) : (
              entities.map((e, i) => (
                <tr key={`${e.source ?? ''}-${e.login}-${i}`}>
                  {hasSource && (
                    <td>
                      <span className={`badge ${e.source === 'LP' ? 'bg-warning-transparent' : 'bg-primary-transparent'}`}>{e.source}</span>
                    </td>
                  )}
                  <td>{e.login}</td>
                  <td className="font-monospace">{vol(side === 'long' ? e.longVolume : e.shortVolume)}</td>
                  <td className="font-monospace">{signedSwap(side === 'long' ? e.longSwap : e.shortSwap)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
